// React
import React, { useEffect, useState } from "react";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// MUI
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

// Third party imports
import { useTranslation } from "react-i18next";

// Components
import ColorPicker from "components/ui/ColorPicker.jsx";
import FormField from "components/ui/FormField";

// APIs
import { getTeams } from "api/teams.jsx";

/**
 * Create or edit a board: name, color, optional team and the
 * "new clients of the team land here" automation.
 * @param {Object|null} props.board - Board to edit, null to create
 * @param {function} props.onSave - async (data) => void
 * @param {function} props.onDelete - async () => void (edit only)
 */
export default function BoardSettingsModal({ open, onClose, board = null, onSave, onDelete }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [chosenColor, setChosenColor] = useState({ color: "#000000", bgcolor: "#FFFFFF" });
  const [autoAdd, setAutoAdd] = useState(false);
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(board?.name || "");
    setTeamId(board?.team?.id || "");
    setChosenColor({
      color: board?.color || "#000000",
      bgcolor: board?.bgcolor || "#FFFFFF",
    });
    setAutoAdd(Boolean(board?.auto_add_new_clients));
    setConfirmDelete(false);
    getTeams()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTeams([...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      })
      .catch(() => setTeams([]));
  }, [open, board]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        team: teamId || null,
        bgcolor: chosenColor.bgcolor,
        auto_add_new_clients: Boolean(teamId) && autoAdd,
      });
      onClose();
    } catch {
      // error already shown by the caller
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch {
      // error already shown by the caller
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { component: "form", onSubmit: handleSubmit } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1.5 }}>
        <span className="min-w-0 flex-1 truncate">
          {board ? t("teamBoard:boards.settingsTitle") : t("teamBoard:boards.createTitle")}
        </span>
        <IconButton size="small" onClick={onClose} aria-label={t("common:close")}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className="space-y-4">
          <FormField label={t("teamBoard:boards.name")} htmlFor="board-name" required>
            <input
              id="board-name"
              type="text"
              value={name}
              maxLength={100}
              autoFocus
              required
              onChange={(e) => setName(e.target.value)}
              placeholder={t("teamBoard:boards.namePlaceholder")}
              className="field"
            />
          </FormField>

          <div>
            <span className="field-label">{t("teamBoard:boards.color")}</span>
            <ColorPicker chosenColor={chosenColor} handleColorChange={setChosenColor} />
          </div>

          <FormField
            label={t("teamBoard:boards.team")}
            htmlFor="board-team"
            hint={t("teamBoard:boards.teamHelp")}
          >
            <select
              id="board-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="field"
            >
              <option value="">{t("teamBoard:boards.noTeam")}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </FormField>

          <label
            className={`flex cursor-pointer items-start gap-2 text-sm ${teamId ? "text-gray-700" : "text-gray-400"}`}
          >
            <input
              type="checkbox"
              className="mt-0.5 shrink-0"
              disabled={!teamId}
              checked={Boolean(teamId) && autoAdd}
              onChange={(e) => setAutoAdd(e.target.checked)}
            />
            <span className="min-w-0">
              {t("teamBoard:boards.autoAdd")}
              <span className="field-hint block">{t("teamBoard:boards.autoAddHelp")}</span>
            </span>
          </label>

          {!board && <p className="field-hint">{t("teamBoard:boards.defaultColumnsHint")}</p>}
        </div>
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap" }}>
        {board && onDelete && (
          <Button
            type="button"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            sx={{ mr: "auto" }}
          >
            {confirmDelete ? t("teamBoard:boards.confirmDelete") : t("teamBoard:boards.delete")}
          </Button>
        )}
        <Button type="button" variant="outlined" color="inherit" onClick={onClose}>
          {t("common:cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!name.trim() || saving}
          loading={saving}
          loadingPosition="start"
        >
          {saving ? t("common:saving") : board ? t("common:save") : t("common:create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
